import { Box, Stack, Typography } from '@cmg/design-system';
import React from 'react';

import { onDark } from '../consentSurface';

/**
 * Truncate an overlong origin from the *left*, so the ellipsis eats the scheme and the leading
 * subdomains and never the registrable domain at the end.
 *
 * `direction: rtl` only moves which end overflows; the text itself is one left-to-right run and
 * renders in order. That holds because `URL.origin` is always ASCII — an international host arrives
 * punycoded as `xn--…` — so there is no bidirectional content here for the property to reorder.
 */
const truncateFromLeft = {
  direction: 'rtl',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/**
 * The URI split into an origin and the rest, or nothing when it cannot be split.
 *
 * That is not only the unparseable case: a scheme the URL standard does not treat as special —
 * `com.example.app://`, or a `urn:` — has an *opaque* origin, which serializes to the literal
 * string "null" and drops the authority on the floor. Splitting those would render `null/callback`
 * for a URI whose real target is `oauth`.
 */
const parseRedirectUri = (redirectUri: string): URL | undefined => {
  try {
    const parsed = new URL(redirectUri);
    return parsed.origin === 'null' ? undefined : parsed;
  } catch {
    return undefined;
  }
};

/**
 * The redirect target, on one line, with the host the part that survives.
 *
 * Rendering the URI as one wrapped string put the line break inside the host — `https://claude.` /
 * `ai.evil.example/...` — and the break position is a function of string length at a known width, so
 * the app choosing the URI also chose where it fell. The registrable domain is the single fact
 * separating a legitimate grant from handing the code to someone else.
 *
 * Origin and path are therefore two nodes on one line with opposite shrink behaviour: the path
 * absorbs every pixel of shortfall first and ellipsises at its end, and only once it has collapsed
 * entirely does the origin give way, from the *left*, sacrificing the scheme and leading subdomains
 * rather than the domain. Nothing the app can put in the URI moves a break into the host.
 *
 * A URI `parseRedirectUri` cannot split is shown whole rather than guessed at.
 */
export const RedirectTarget: React.FC<{ redirectUri: string }> = ({ redirectUri }) => {
  const url = parseRedirectUri(redirectUri);

  const origin = url?.origin ?? '';
  const path = url ? `${url.pathname}${url.search}${url.hash}` : '';

  return (
    <Box sx={{ ...onDark.claim, mt: 4, overflow: 'hidden' }}>
      <Typography variant="body1" component="p" sx={onDark.mutedText}>
        Authorizing will redirect to
      </Typography>
      {url ? (
        <Stack direction="row" alignItems="baseline" mt={0.5} maxWidth="100%">
          <Typography
            variant="body1"
            component="span"
            sx={{
              ...onDark.mono,
              ...truncateFromLeft,
              color: 'common.white',
              flex: '0 0 auto',
              maxWidth: '100%',
            }}
          >
            {origin}
          </Typography>
          {path && path !== '/' && (
            <Typography
              variant="body1"
              component="span"
              sx={{
                ...onDark.mono,
                color: 'common.white',
                flex: '0 1 auto',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {path}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography
          variant="body1"
          component="p"
          sx={{ ...onDark.mono, color: 'common.white', wordBreak: 'break-all', mt: 0.5 }}
        >
          {redirectUri}
        </Typography>
      )}
    </Box>
  );
};

export default RedirectTarget;
